import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HiOutlineCheckCircle } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import Button from '../../../../../components/Button';
import WeatherTemperaturePlot, { WeatherPlotChannel } from '../../WeatherTemperaturePlot';
import { useUpdateWeatherData } from '../../../../../hooks/useSessions';
import { WeatherChannelKey, WeatherSourceKey } from '../../../../../types/Session';
import OptionSection from '../OptionSection';
import ForceSyntheticDpControl from '../../../../../components/ForceSyntheticDpControl';
import { withForceSyntheticDp } from '../../../../../utils/forceSyntheticDp';
import SourceChannelMatrix from './SourceChannelMatrix';
import ManualWeatherTable, { ManualRowValues } from './ManualWeatherTable';
import RunDensityTable from './RunDensityTable';

interface MeteorologicalSourceSectionProps {
  sessionId: string;
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

const PLOT_CHANNELS: { key: WeatherPlotChannel; label: string }[] = [
  { key: 'Temp_T', label: 'Temp' },
  { key: 'Baro_P', label: 'Baro' },
  { key: 'Humidity_x', label: 'RH' },
  { key: 'AirDensity', label: 'ρ' },
];

export default function MeteorologicalSourceSection({
  sessionId,
  session,
  updateSession,
  invalidateSession,
}: MeteorologicalSourceSectionProps): ReactElement {
  const updateWeatherData = useUpdateWeatherData(sessionId);

  const [meteorologicalSource, setMeteorologicalSource] = useState<string>('Weather Api');
  const [meteorologicalSourceChanged, setMeteorologicalSourceChanged] = useState(false);
  const [isUpdatingWeatherSource, setIsUpdatingWeatherSource] = useState(false);
  const [isSavingChannelSelection, setIsSavingChannelSelection] = useState(false);
  const [expandedWeatherEdit, setExpandedWeatherEdit] = useState<string | null>(null);
  const [isUpdatingProbeOffset, setIsUpdatingProbeOffset] = useState(false);
  const [isUpdatingManualWeather, setIsUpdatingManualWeather] = useState(false);
  const [isSavingForceSyntheticDp, setIsSavingForceSyntheticDp] = useState(false);
  const [editingWeatherRow, setEditingWeatherRow] = useState<number | null>(null);
  const [plotChannel, setPlotChannel] = useState<WeatherPlotChannel>('Temp_T');

  const showBikeComputerOption = Boolean(
    session?.weather_controller_dict?.bike_computer ||
      session?.weather_controller_dict?.selection === 'bike_computer' ||
      session?.meteorological_source === 'Bike Computer' ||
      meteorologicalSource === 'Bike Computer',
  );

  const meteorologicalSourceSelectId = 'meteorological-source-select';

  const manualWeatherRows = useMemo(() => {
    const manualData = session?.weather_controller_dict?.manual?.weather_data;
    if (!manualData) {
      return [];
    }

    const rows = Array.isArray(manualData) ? manualData : [manualData];
    return [...rows].sort((a: any, b: any) => (a?.TimeAbs_t ?? 0) - (b?.TimeAbs_t ?? 0));
  }, [session]);

  const sessionProbeOffset = session?.weather_controller_dict?.offsets_raw?.probe?.Temp_T;

  // Set initial meteorological source from session data
  useEffect(() => {
    if (session?.weather_controller_dict?.selection) {
      // Map backend selection keys to frontend display names
      const selectionMap: Record<string, string> = {
        weather_api: 'Weather Api',
        probe: 'Probe',
        probe_hybrid: 'Probe-API',
        local_station: 'Local Weather Station',
        manual: 'Manual',
        bike_computer: 'Bike Computer',
      };
      const selectedSource = selectionMap[session.weather_controller_dict.selection];
      if (selectedSource) {
        setMeteorologicalSource(selectedSource);
      }
    } else if (session?.meteorological_source) {
      // Fallback to meteorological_source if selection is not available
      setMeteorologicalSource(session.meteorological_source);
    }
  }, [session]);

  const backendKeyMap: Record<string, string> = {
    'Weather Api': 'weather_api',
    Probe: 'probe',
    'Probe-API': 'probe_hybrid',
    'Local Weather Station': 'local_station',
    Manual: 'manual',
    'Bike Computer': 'bike_computer',
  };

  // Handle meteorological source change (session-wide default / wind source)
  const handleMeteorologicalSourceChange = (newSource: string) => {
    setMeteorologicalSource(newSource);
    const newBackendKey = backendKeyMap[newSource];
    const currentBackendKey = session?.weather_controller_dict?.selection;
    setMeteorologicalSourceChanged(newBackendKey !== currentBackendKey);
  };

  // Handle updating the weather source selection
  const handleUpdateWeatherSource = async () => {
    const backendKey = backendKeyMap[meteorologicalSource];

    try {
      setIsUpdatingWeatherSource(true);
      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...session.weather_controller_dict,
          selection: backendKey,
        },
      });
      toast.success('Weather source updated');
      setMeteorologicalSourceChanged(false);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating weather source');
    } finally {
      setIsUpdatingWeatherSource(false);
    }
  };

  // Handle saving per-channel source overrides from the matrix
  const handleSaveChannelSelection = async (
    channelSelection: Partial<Record<WeatherChannelKey, WeatherSourceKey>>,
  ) => {
    try {
      setIsSavingChannelSelection(true);
      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...session.weather_controller_dict,
          channel_selection: channelSelection,
        },
      });
      toast.success('Channel sources updated');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating channel sources');
    } finally {
      setIsSavingChannelSelection(false);
    }
  };

  // Handle toggling the session-wide synthetic-DP forcing flag
  // (parser_vars.atmosphere.force_synthetic_dp; run-level overrides win)
  const handleSaveForceSyntheticDp = async (next: boolean | null) => {
    try {
      setIsSavingForceSyntheticDp(true);
      await updateSession.mutateAsync({
        parser_vars: withForceSyntheticDp(session?.parser_vars, next),
      });
      toast.success('Synthetic DP setting saved');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error saving synthetic DP setting');
    } finally {
      setIsSavingForceSyntheticDp(false);
    }
  };

  // Handle updating Weather Api data
  const handleUpdateWeatherApi = async () => {
    try {
      await updateWeatherData.mutateAsync();
      toast.success('Weather API data updated');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating weather API data');
    }
  };

  // Handle updating probe temperature offset
  const handleUpdateProbeOffset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!session) {
      toast.error('Session data not available');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const offsetValue = ((formData.get('probeOffset') as string) || '').trim();

    if (offsetValue === '' || offsetValue === '-' || offsetValue === '.') {
      toast.error('Temperature offset is required');
      return;
    }
    if (!/^-?\d+(\.\d+)?$/.test(offsetValue)) {
      toast.error('Temperature offset must be a valid number');
      return;
    }
    const offset = Number(offsetValue);
    if (Number.isNaN(offset)) {
      toast.error('Temperature offset must be a valid number');
      return;
    }

    try {
      setIsUpdatingProbeOffset(true);

      const updatedOffsetsRaw = {
        ...session.weather_controller_dict.offsets_raw,
        probe: {
          ...session.weather_controller_dict.offsets_raw?.probe,
          Temp_T: offset,
        },
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...session.weather_controller_dict,
          offsets_raw: updatedOffsetsRaw,
        },
      });

      toast.success('Probe temperature offset updated');
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating probe temperature offset');
    } finally {
      setIsUpdatingProbeOffset(false);
    }
  };

  // Handle updating manual weather data (humidity percent, optional baro Pa)
  const handleSaveManualWeather = async ({ hour, minute, temperature, humidityPct, baroPa }: ManualRowValues) => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    try {
      setIsUpdatingManualWeather(true);

      // Interpret the entered hour:minute as the recording-location wall clock
      // (matching the weather plots, which shift by session.utc_offset), then
      // convert to a UTC epoch for storage by removing that offset. Date.UTC is
      // used so the viewer's browser zone never enters the calculation.
      const sessionDate = session?.date || new Date().toISOString().split('T')[0];
      const [yearStr, monthStr, dayStr] = sessionDate.split('-');
      const utcOffsetSeconds = Number(session?.utc_offset ?? 0);
      const wallClockMs = Date.UTC(Number(yearStr), Number(monthStr) - 1, Number(dayStr), hour, minute, 0);
      const TimeAbs_t = Math.floor(wallClockMs / 1000) - utcOffsetSeconds;

      const manualWeatherData = {
        TimeAbs_t: TimeAbs_t,
        Temp_T: temperature,
        Baro_P: baroPa,
        Humidity_x: humidityPct,
        WindDir_A: null,
        Wind_v: null,
      };

      // Get existing manual data or create new array
      const existingManualData = manualWeatherRows.map((row: any) => ({ ...row }));

      // When editing, remove the entry being edited (by editingWeatherRow timestamp)
      // When adding new (-1), just filter out any duplicate at the new timestamp
      const timestampToRemove = editingWeatherRow === -1 ? manualWeatherData.TimeAbs_t : editingWeatherRow;
      const dedupedManualData = existingManualData.filter((row: any) => row?.TimeAbs_t !== timestampToRemove);

      // If editing and time changed, also remove any entry at the new timestamp to prevent duplicates
      const finalManualData =
        editingWeatherRow !== -1 && editingWeatherRow !== manualWeatherData.TimeAbs_t
          ? dedupedManualData.filter((row: any) => row?.TimeAbs_t !== manualWeatherData.TimeAbs_t)
          : dedupedManualData;

      const updatedManualData = [...finalManualData, manualWeatherData].sort((a, b) => a.TimeAbs_t - b.TimeAbs_t);

      const manualObject = {
        ...(session.weather_controller_dict?.manual || {}),
        ts: Math.floor(Date.now() / 1000),
        weather_data: updatedManualData,
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...(session.weather_controller_dict || {}),
          manual: manualObject,
        },
      });

      toast.success('Manual weather data saved');
      setEditingWeatherRow(null);
      await invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error adding manual weather data');
    } finally {
      setIsUpdatingManualWeather(false);
    }
  };

  const handleRemoveManualWeatherRow = async (timeAbs: number) => {
    if (!session) {
      toast.error('Session data not available');
      return;
    }

    const filteredRows = manualWeatherRows.filter((row: any) => row?.TimeAbs_t !== timeAbs);

    if (filteredRows.length === manualWeatherRows.length) {
      toast.error('Manual entry not found');
      return;
    }

    try {
      setIsUpdatingManualWeather(true);

      const manualObject = {
        ...(session.weather_controller_dict?.manual || {}),
        ts: Math.floor(Date.now() / 1000),
        weather_data: filteredRows,
      };

      await updateSession.mutateAsync({
        weather_controller_dict: {
          ...(session.weather_controller_dict || {}),
          manual: manualObject,
        },
      });

      toast.success('Manual weather entry removed');
      if (editingWeatherRow === timeAbs) {
        setEditingWeatherRow(null);
      }
      await invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error removing manual weather data');
    } finally {
      setIsUpdatingManualWeather(false);
    }
  };

  return (
    <OptionSection
      title="Meteorological Source"
      status={<span className="cr-text-sm cr-text-gray-500">{meteorologicalSource}</span>}
    >
      {/* Weather plot + source matrix */}
      <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md lg:cr-flex-row">
        {/* Channel-switchable weather plot - Left Side */}
        {/* min-w-0: without it this flex child's automatic min-width is its
            content width, so the plot refuses to shrink and squeezes the
            matrix panel below its declared width, clipping the last column. */}
        <div className="cr-flex cr-min-w-0 cr-flex-1 cr-flex-col cr-gap-4">
          <div className="cr-flex cr-items-center cr-gap-1 cr-self-end cr-rounded-md cr-bg-gray-100 cr-p-1">
            {PLOT_CHANNELS.map((ch) => (
              <button
                key={ch.key}
                type="button"
                onClick={() => setPlotChannel(ch.key)}
                className={`cr-rounded cr-px-3 cr-py-1 cr-text-sm ${
                  plotChannel === ch.key
                    ? 'cr-bg-white cr-font-semibold cr-text-gray-900 cr-shadow'
                    : 'cr-text-gray-600 hover:cr-text-gray-900'
                }`}
              >
                {ch.label}
              </button>
            ))}
          </div>
          <WeatherTemperaturePlot
            weatherControllerDict={session?.weather_controller_dict || null}
            selectedSource={meteorologicalSource}
            channel={plotChannel}
            resolvedWeather={session?.resolved_weather || null}
            utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
          />
          <div className="cr-flex cr-flex-wrap cr-items-end cr-justify-between cr-gap-2">
            <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
              <label
                htmlFor={meteorologicalSourceSelectId}
                className="cr-text-sm cr-font-medium cr-text-gray-700"
                title="Session-wide default source; wind always follows this. Temp/Baro/Humidity follow the matrix."
              >
                Default / Wind Source
              </label>
              <select
                id={meteorologicalSourceSelectId}
                className="cr-h-11 cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-4 cr-text-base focus:cr-border-blue-500 focus:cr-outline-none"
                value={meteorologicalSource}
                onChange={(e) => handleMeteorologicalSourceChange(e.target.value)}
              >
                <option value="Weather Api">Weather Api</option>
                <option value="Probe">Probe</option>
                <option value="Probe-API">Probe-API</option>
                <option value="Local Weather Station">Local Weather Station</option>
                <option value="Manual">Manual</option>
                {showBikeComputerOption && <option value="Bike Computer">Bike Computer</option>}
              </select>
            </div>
            {meteorologicalSourceChanged && (
              <Button
                onClick={handleUpdateWeatherSource}
                disabled={isUpdatingWeatherSource}
                icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5 cr-stroke-[2.2px]" />}
                normalCase
                className="cr-h-11 cr-font-semibold"
              >
                {isUpdatingWeatherSource ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
        </div>

        {/* Per-channel source matrix - Right Side */}
        <div
          className="cr-flex cr-flex-col cr-justify-center lg:cr-w-[30rem] lg:cr-flex-shrink-0
                     lg:cr-border-l lg:cr-border-gray-200 lg:cr-pl-4"
        >
          <SourceChannelMatrix
            wcd={session?.weather_controller_dict || null}
            resolvedWeather={session?.resolved_weather}
            saving={isSavingChannelSelection}
            onSaveChannelSelection={handleSaveChannelSelection}
            showBikeComputer={showBikeComputerOption}
            expandedWeatherEdit={expandedWeatherEdit}
            onToggleEdit={(name) => setExpandedWeatherEdit(expandedWeatherEdit === name ? null : name)}
          />
        </div>
      </div>

      {/* Expandable Edit Cards */}
      {expandedWeatherEdit === 'Weather Api' && (
        <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
          <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Update Weather Api Data</h5>
          <p className="cr-mb-3 cr-text-sm cr-text-gray-600">
            Fetch the latest weather data from the Weather API service.
          </p>
          <Button
            onClick={handleUpdateWeatherApi}
            icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5" />}
          >
            Update Weather Api
          </Button>
        </div>
      )}
      {expandedWeatherEdit === 'Probe' && (
        <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
          <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Probe Temperature Offset</h5>
          <p className="cr-mb-3 cr-text-sm cr-text-gray-600">
            Adjust the temperature offset for the probe sensor.
          </p>
          <form onSubmit={handleUpdateProbeOffset}>
            <div className="cr-mb-4 cr-flex cr-items-center cr-gap-3">
              <label htmlFor="probeOffset" className="cr-text-sm cr-font-medium cr-text-gray-700">
                Temperature Offset (°C):
              </label>
              <input
                id="probeOffset"
                name="probeOffset"
                type="text"
                inputMode="decimal"
                defaultValue={sessionProbeOffset ?? 0}
                placeholder="e.g. 0.5"
                className="cr-w-24 cr-rounded cr-border cr-border-gray-300 cr-px-3 cr-py-2 focus:cr-border-blue-500 focus:cr-outline-none"
              />
            </div>
            <Button
              type="submit"
              disabled={isUpdatingProbeOffset}
              icon={<HiOutlineCheckCircle className="cr-h-5 cr-w-5" />}
              className="cr-text-sm"
            >
              {isUpdatingProbeOffset ? 'Updating...' : 'Update Offset'}
            </Button>
          </form>
        </div>
      )}
      {expandedWeatherEdit === 'Local Weather Station' && (
        <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
          <h5 className="cr-mb-3 cr-font-semibold cr-text-gray-800">Local Weather Station Settings</h5>
          <p className="cr-text-sm cr-text-gray-600">Configure local weather station data source.</p>
        </div>
      )}
      {expandedWeatherEdit === 'Manual' && (
        <ManualWeatherTable
          rows={manualWeatherRows}
          utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
          editingWeatherRow={editingWeatherRow}
          isUpdating={isUpdatingManualWeather}
          onSave={handleSaveManualWeather}
          onEditRow={(row) => setEditingWeatherRow(row?.TimeAbs_t)}
          onRemoveRow={handleRemoveManualWeatherRow}
          onAddRow={() => setEditingWeatherRow(-1)}
          onCancelEdit={() => setEditingWeatherRow(null)}
        />
      )}

      {/* Synthetic dynamic-pressure forcing (session-wide; runs can override) */}
      <div className="cr-mt-4 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
        <ForceSyntheticDpControl
          scope="session"
          value={session?.parser_vars?.atmosphere?.force_synthetic_dp}
          saving={isSavingForceSyntheticDp}
          onSave={handleSaveForceSyntheticDp}
        />
        <div className="cr-mt-1 cr-text-xs cr-text-gray-400">
          Takes effect on reprocess. Individual runs can override this from their Health Check page.
        </div>
      </div>

      {/* Per-run density + provenance */}
      <RunDensityTable session={session} />
    </OptionSection>
  );
}
